// Phase 17 (2026-07-02) — Onboarding & System Guide modal.
//
// Goal: first-login users get a 1-click skippable wizard explaining the app's
// six layers (setup → entry → workflow → posting → reports → audit). A "Help"
// icon in the top bar re-opens this guide any time. Dismissal state is
// per-user via localStorage (key: `st_onboarding_${user.id}` =
// "seen" | "never"). The modal never blocks usage.

import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  HelpCircle, X, ChevronRight, ChevronLeft, BookOpen, Workflow,
  ClipboardList, BadgeCheck, BarChart3, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  {
    icon: BookOpen, title: "1. Setup the books",
    body: "Configure the Chart of Accounts (CoA), Bank & Cash accounts, Suppliers, Customers, Employees, Tax Settings, Payroll defaults, and your Company Profile. This is the foundation everything else relies on.",
  },
  {
    icon: ClipboardList, title: "2. Enter transactions",
    body: "Record day-to-day activity: Expenses, Bills, Connected Invoices, Bank Deposits/Withdrawals, Petty Cash, Payroll Runs, Tax & Loan Payments. AI Quick Add can pre-fill drafts from a natural-language prompt — it never auto-posts.",
  },
  {
    icon: Workflow, title: "3. Approval workflow",
    body: "Every transaction flows through: Draft → Submitted → Under Review → Verified → Processed → Posted. Verification and posting are role-gated. Audit trail records every step.",
  },
  {
    icon: BadgeCheck, title: "4. Posting effect",
    body: "Once posted, the transaction's Journal Entry hits the GL and ripples through AP/AR, Bank & Cash, Cashbook, Petty Cash, Payroll, Loans, Taxes, Reports, and the Dashboard. Trial Balance always stays Σ Dr = Σ Cr.",
  },
  {
    icon: BarChart3, title: "5. Reports & insights",
    body: "21 audit-grade reports (Trial Balance, P&L, Balance Sheet, Cash Flow, Bank Recon, AP/AR Aging, VAT/WHT/CIT, Loan Summary, Payroll, Audit Trail…). All branded, all exportable to PDF + Excel.",
  },
  {
    icon: ShieldCheck, title: "6. Controls & audit",
    body: "Approvals, Internal Controls, Audit Logs and Observer dashboards keep every change traceable. System Administrators can override edits/deletes with a required Reason — reversing JEs are auto-posted to keep the books true.",
  },
];

function _storageKey(userId) {
  return `st_onboarding_${userId || "anon"}`;
}

export function useOnboardingGate() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    const v = localStorage.getItem(_storageKey(user.id));
    if (!v) setOpen(true);
  }, [user]);
  return { open, setOpen };
}

export default function OnboardingModal({ open, onClose }) {
  const { user } = useAuth();
  const [idx, setIdx] = useState(0);

  const close = (forever = false) => {
    if (user) {
      localStorage.setItem(_storageKey(user.id), forever ? "never" : "seen");
    }
    setIdx(0);
    onClose?.();
  };

  const step = STEPS[idx];
  const Icon = step.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close(false)}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-[#4FA3D1]" />
            {step.title}
          </DialogTitle>
          <DialogDescription className="pt-2 text-slate-700 text-[14px] leading-relaxed">
            {step.body}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 justify-center py-2">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#4FA3D1]" : "w-2 bg-slate-300"}`}
            />
          ))}
        </div>
        <DialogFooter className="flex !justify-between gap-2 pt-2">
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => close(true)} data-testid="onboarding-never">
              <X className="h-4 w-4 mr-1" /> Do not show again
            </Button>
            <Button variant="ghost" size="sm" onClick={() => close(false)} data-testid="onboarding-skip">
              Skip for now
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx((n) => Math.max(0, n - 1))}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {idx < STEPS.length - 1 ? (
              <Button size="sm" className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                      onClick={() => setIdx((n) => n + 1)} data-testid="onboarding-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                      onClick={() => close(false)} data-testid="onboarding-finish">
                Got it
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HelpButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost" size="icon"
        title="System guide"
        data-testid="help-system-guide-btn"
        onClick={() => setOpen(true)}
      >
        <HelpCircle className="h-5 w-5 text-slate-500" />
      </Button>
      <OnboardingModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
